import assign from "lodash.assign";
import Entitlements from "services/Entitlements";
import { LOCATION_CHANGE } from "react-router-redux";
import ERROR_MESSAGES from "common/constants/ErrorMessages";
import { buildPayment } from "common/utils/payment";
import { setErrorInErrors, clearErrorFromErrors } from "utils/errorsUtil";
import REDUX_ERROR_TYPES from "constants/ReduxErrorTypes";

const DEFAULT_STATE = {
    subscriptions: [],
    invoices: [],
    errors: {}
};

// Actions (must be name-spaced for reducer)

export const ENTITLEMENTS_ADD_SUBSCRIPTION_SUCCESS = "ENTITLEMENTS_ADD_SUBSCRIPTION_SUCCESS";
export const ENTITLEMENTS_SHOW_ERROR = "ENTITLEMENTS_SHOW_ERROR";
export const ENTITLEMENTS_CLEAR_ERROR = "ENTITLEMENTS_CLEAR_ERROR";
export const ENTITLEMENTS_ALREADY_LINKED = "ENTITLEMENTS_ALREADY_LINKED";
export const ENTITLEMENTS_REFRESH_SUBSCRIPTIONS = "ENTITLEMENTS_REFRESH_SUBSCRIPTIONS";
export const ENTITLEMENTS_SET_INVOICES = "ENTITLEMENTS_SET_INVOICES";
export const ENTITLEMENTS_UPDATE_EDIT_PAYMENT_MODE = "ENTITLEMENTS_UPDATE_EDIT_PAYMENT_MODE";

// Reducer

export function entitlementsReducer(state = DEFAULT_STATE, action = {}) {
    switch (action.type) {
        case LOCATION_CHANGE:
            return assign({}, DEFAULT_STATE, {
                subscriptions: state.subscriptions,
                isReferral: state.isReferral,
                isLinked: state.isLinked,
                errors: state.errors
            });
        case ENTITLEMENTS_REFRESH_SUBSCRIPTIONS:
            return assign({}, state, {
                subscriptions: action.subscriptions || []
            });
        case ENTITLEMENTS_SET_INVOICES:
            return assign({}, state, {
                invoices: action.invoices || []
            });
        case ENTITLEMENTS_ADD_SUBSCRIPTION_SUCCESS:
            return assign({}, state, {
                subscriptions: action.subscriptions,
                isLinked: true,
                successMessage: action.successMessage
            });
        case ENTITLEMENTS_ALREADY_LINKED:
            return assign({}, state, {
                isLinked: true,
                successMessage: action.successMessage
            });
        case ENTITLEMENTS_SHOW_ERROR:
            return assign({}, state, {
                errors: setErrorInErrors(state.errors, action.error)
            });
        case ENTITLEMENTS_CLEAR_ERROR:
            return assign({}, state, {
                errors: clearErrorFromErrors(state.errors, action.errorType)
            });
        case ENTITLEMENTS_UPDATE_EDIT_PAYMENT_MODE:
            return assign({}, state, {
                isEditingPayment: action.isEditingPayment
            });
        default:
            return state;
    }
}

// Action Creators

export function showError(error) {
    return {
        type: ENTITLEMENTS_SHOW_ERROR,
        error
    };
}

export function clearError(errorType) {
    return {
        type: ENTITLEMENTS_CLEAR_ERROR,
        errorType
    };
}

export function refreshSubscriptions(subscriptions) {
    return {
        type: ENTITLEMENTS_REFRESH_SUBSCRIPTIONS,
        subscriptions
    };
}

export function setInvoices(invoices) {
    return {
        type: ENTITLEMENTS_SET_INVOICES,
        invoices
    };
}

export function addSubscriptionSuccess(subscriptions) {
    return {
        type: ENTITLEMENTS_ADD_SUBSCRIPTION_SUCCESS,
        successMessage: "Your subscription is linked.",
        subscriptions
    };
}

export function showAlreadyLinked() {
    return {
        type: ENTITLEMENTS_ALREADY_LINKED,
        successMessage: "Your subscription is linked."
    };
}

export function getSubscriptions({ showAccounts, showRatePlans, activeOnly, bulkSubscriptionActive } = {}) {
    return (dispatch) => {
        return Entitlements.getSubscriptions({ showAccounts, showRatePlans, activeOnly, bulkSubscriptionActive })
            .catch(() => {
                dispatch(showError({
                    type: REDUX_ERROR_TYPES.GET_SUBSCRIPTIONS,
                    message: ERROR_MESSAGES.ENTITLEMENTS_SHOW_GET_SUBSCRIPTIONS_ERROR
                }));

                return [];
            })
            .then((subscriptions) => {
                dispatch(refreshSubscriptions(subscriptions));

                return subscriptions;
            });
    };
}

export function getUserInvoices() {
    return (dispatch) => {
        return Entitlements.getUserInvoices()
            .then((response) => {
                dispatch(setInvoices(response.invoices));
                dispatch(clearError(REDUX_ERROR_TYPES.FILE_DOWNLOAD));
            })
            .catch((error) => {
                dispatch(showError({
                    type: REDUX_ERROR_TYPES.INVOICES,
                    message: ERROR_MESSAGES.ENTITLEMENTS_SHOW_BILLING_HISTORY_LOAD_ERROR
                }));

                throw error;
            });
    };
}

export function addBusinessweekSubscription(subscription) {
    return (dispatch) => {
        return Entitlements.addBusinessweekSubscription(subscription)
            .catch((error) => {
                dispatch(showError({
                    type: REDUX_ERROR_TYPES.LINK_BUSINESSWEEK,
                    message: ERROR_MESSAGES.ENTITLEMENTS_SHOW_LINKING_ERROR
                }));

                throw error;
            })
            .then(() => Entitlements.getSubscriptions())
            .then((subscriptions) => dispatch(addSubscriptionSuccess(subscriptions)));
    };
}

export function updateSubscription(subscriptionId, productRatePlanId) {
    return (dispatch) => {
        return Entitlements.updateSubscription(subscriptionId, productRatePlanId)
            .catch((error) => {
                dispatch(showError({
                    type: REDUX_ERROR_TYPES.UPDATE_SUBSCRIPTION,
                    message: ERROR_MESSAGES.ENTITLEMENTS_SHOW_UPGRADE_ERROR
                }));

                throw error;
            });
    };
}

export function updateSubscriptionPaymentMethod(subscription) {
    return (dispatch, getState) => {
        const {
            paymentForm
        } = getState();

        const paymentMethod = buildPayment(paymentForm);

        return Entitlements.updateSubscriptionPaymentMethod(paymentMethod, subscription.id)
            .then(() => {
                dispatch(updateEditPaymentMode(false));
                clearError(REDUX_ERROR_TYPES.UPDATE_PAYMENT);
            })
            .catch((error) => {
                dispatch(showError({
                    type: REDUX_ERROR_TYPES.UPDATE_PAYMENT,
                    message: error.errorMessage
                }));

                throw error;
            });
    };
}

export function editPayment() {
    return (dispatch, getState) => {
        if (getState().entitlements.isEditingPayment) {
            dispatch(updateEditPaymentMode(false));

            return;
        }

        dispatch(updateEditPaymentMode(true));
    };
}

export function updateEditPaymentMode(isEditingPayment) {
    return {
        type: ENTITLEMENTS_UPDATE_EDIT_PAYMENT_MODE,
        isEditingPayment
    };
}

export default {
    entitlementsReducer,
    getSubscriptions,
    addBusinessweekSubscription,
    updateSubscription,
    showAlreadyLinked,
    refreshSubscriptions,
    getUserInvoices,
    updateSubscriptionPaymentMethod,
    editPayment,
    showError,
    clearError
};
